import {
  ICheckInvoiceStatusReq,
  ICheckInvoiceStatusRes,
  ICheckStudentHasSubmittedExamResult,
  ICheckStudentHasSubmittedExamResultRes,
  IDeleteExamResult,
  IDeleteExamResultAttachment,
  IExamResultFormData,
  IForgotPasswordRes,
  IGetExamTypeRes,
  IGetNotificationsRes,
  IGetOtpCodeFormData,
  IGetOtpCodeRes,
  IGetStudentOnboardingMasteryLevelPermissionRes,
  IGetStudentOnboardingTooltipPermissionRes,
  IGetSubjectSubscriptionRes,
  IGetTopUpGenieCreditList,
  IGoToBubbleStoreRes,
  ILoginRes,
  IMarkReadNotifRes,
  IPlanOverviewDetailReq,
  IPlanOverviewDetailRes,
  IRequestPaymentLinkRes,
  IResetPasswordRes,
  ISubmitExamResultData,
  ITopUpWorksheetCreditReq,
  ITopUpWorksheetCreditResp,
  IUpdateRenewalReq,
  IUserDetailRes,
  IUserForgotPasswordReq,
  IUserLoginReq,
  IUserResetPasswordReq,
  IUserResetPasswordTokenReq,
  IUserUnreadNotificationRes,
  IVerifyOtpCodeFormData,
} from '@models/interfaces/user.interface';
import AUTH_API, { sendRequest } from '@services/auth.service';
import LOCAL_API from '@services/local.service';
import PUBLIC_API from '@services/public.service';
import convertFormData from '@utils/helpers/convertToFormData';
import { IDefaultResponse } from '@models/interfaces/global.interface';

const UserService = {
  async trackNotification(notification_id: number) {
    return await AUTH_API.post<IDefaultResponse>(
      'user/track_notification/json',
      convertFormData({
        notification_id: notification_id,
      }),
    );
  },
  async login(formData: IUserLoginReq) {
    return await PUBLIC_API.post<ILoginRes>(
      `/user/login/json`,
      convertFormData(formData),
    );
  },
  async verifyLogin(uid: string) {
    return await LOCAL_API.post(
      `/login/verify_login`,
      convertFormData({ uid }),
    );
  },
  async userDetail() {
    return await sendRequest<IUserDetailRes>({
      url: `/user/get_detail/json`,
      method: 'GET',
    });
  },
  async forgotPassword(formData: IUserForgotPasswordReq) {
    return await PUBLIC_API.post<IForgotPasswordRes>(
      `/user/forgot_password/json`,
      convertFormData(formData),
    );
  },
  async resetPassword(formData: IUserResetPasswordReq) {
    return await PUBLIC_API.post<IResetPasswordRes>(
      `/user/submit_forgot_password`,
      convertFormData(formData),
    );
  },
  async validateResetPasswordToken(formData: IUserResetPasswordTokenReq) {
    return await PUBLIC_API.post<IDefaultResponse>(
      `/user/validate_forgot_password/json`,
      convertFormData(formData),
    );
  },

  async getSchoolList() {
    return await AUTH_API.post(`/user/school_list/json`);
  },

  async getSchoolDetails(school_id: number) {
    return await AUTH_API.get(`/schools/${school_id}`);
  },

  async uploadProfileImage(data: FormData) {
    return await AUTH_API.post<IDefaultResponse>(
      `user/change_profile/json`,
      data,
    );
  },

  async getNotifications(limit: number, pageParam?: number) {
    return await AUTH_API.post<IGetNotificationsRes>(
      'user/get_notifications/json',
      convertFormData({
        older_id: pageParam ?? '',
        load_limit: limit,
      }),
    );
  },
  async userUnreadNotification() {
    return await AUTH_API.post<IUserUnreadNotificationRes>(
      'user/get_unread_notif/json',
    );
  },
  async markNotificationAsRead() {
    return await AUTH_API.post<IMarkReadNotifRes>('user/mark_read_notif/json');
  },
  async getSubjectSubscription() {
    return await AUTH_API.get<IGetSubjectSubscriptionRes>(
      `/subscription/subject_subscription/json`,
    );
  },
  async goToBubbleStore() {
    return await AUTH_API.get<IGoToBubbleStoreRes>('/misc/get_bubbly_url/json');
  },
  async updateNPSStatus(action_type: number) {
    return await AUTH_API.post<IDefaultResponse>(
      'user/dismiss_attempt_nps_survey/json',
      convertFormData({
        action_type: action_type,
      }),
    );
  },
  async updateMasterySurveyStatus() {
    return await AUTH_API.post<IDefaultResponse>(
      'user/update_mastery_survey_status/json',
    );
  },
  async getOtpCode(data: IGetOtpCodeFormData) {
    return await PUBLIC_API.post<IGetOtpCodeRes>(
      '/user/get_otp_code/json',
      data,
    );
  },
  async verifyOtpCode(data: IVerifyOtpCodeFormData) {
    return await PUBLIC_API.post<IDefaultResponse>(
      '/user/verify_otp_code/json',
      data,
    );
  },
  async getStudentOnboardingMasteryLevelPermission() {
    return await AUTH_API.post<IGetStudentOnboardingMasteryLevelPermissionRes>(
      'user/get_student_onboarding_mastery_level_permission/json',
    );
  },
  async getStudentOnboardingTooltipPermission() {
    return await AUTH_API.post<IGetStudentOnboardingTooltipPermissionRes>(
      'user/get_student_onboarding_tooltip_permission/json',
    );
  },
  async updateUserProfile(data: object) {
    return await AUTH_API.post(
      'user/update_student_profile/json',
      convertFormData(data),
    );
  },
  async getTopUpGenieCreditList() {
    return await AUTH_API.post<IGetTopUpGenieCreditList>(
      'subscription/self_serve_ws_topup_list/json',
    );
  },
  async topUpWorksheetCredit(formData: ITopUpWorksheetCreditReq) {
    return await AUTH_API.post<ITopUpWorksheetCreditResp>(
      'subscription/self_serve_purchase_worksheet/json',
      convertFormData(formData),
    );
  },
  async getPlanOverviewDetail(formData: IPlanOverviewDetailReq) {
    return await AUTH_API.post<IPlanOverviewDetailRes>(
      'subscription/get_detail_plan/json',
      convertFormData(formData),
    );
  },
  async updateRenewal(formData: IUpdateRenewalReq) {
    return await AUTH_API.post<IDefaultResponse>(
      'subscription/update_renewal/json',
      convertFormData(formData),
    );
  },
  async requestPaymentLink(subjectId: number) {
    return await AUTH_API.post<IRequestPaymentLinkRes>(
      'subscription/request_payment_link/json',
      convertFormData({ subject_id: subjectId }),
    );
  },
  async checkInvoiceStatus(formData: ICheckInvoiceStatusReq) {
    return await AUTH_API.post<ICheckInvoiceStatusRes>(
      'subscription/check_invoice_status/json',
      convertFormData(formData),
    );
  },
  async getExamTypes() {
    return await AUTH_API.post<IGetExamTypeRes>('student/exam_type_list');
  },
  async studentHasUploadedExamResult(
    formData: ICheckStudentHasSubmittedExamResult,
  ) {
    return await AUTH_API.post<ICheckStudentHasSubmittedExamResultRes>(
      'student/student_has_uploaded_exam_result',
      convertFormData(formData),
    );
  },
  async submitExamResult(formData: IExamResultFormData) {
    return await AUTH_API.post<ISubmitExamResultData>(
      'student/submit_score',
      convertFormData(formData),
    );
  },
  async uploadExamResultImages(formData: FormData) {
    return await AUTH_API.post<IDefaultResponse>(
      'student/upload_exam_image',
      formData,
    );
  },
  async deleteExamResult(formData: IDeleteExamResult) {
    return await AUTH_API.post<IDefaultResponse>(
      'student/delete_exam_score',
      convertFormData(formData),
    );
  },
  async deleteExamResultAttachment(formData: IDeleteExamResultAttachment) {
    return await AUTH_API.post<IDefaultResponse>(
      'student/delete_exam_image',
      convertFormData(formData),
    );
  },
};
export default UserService;
